import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ChevronDown, X } from "lucide-react";
import { LINKS, LOGO, MAIN_NAV } from "../../data/site";

const MOBILE_NAV = [...MAIN_NAV.slice(0, 6), { label: "Retreats & Events", to: "/retreatsandevents", key: "retreats" }, MAIN_NAV[6]];
const isActive = (n, p) => (n.to === "/" ? p === "/" : p === n.to || (!!n.children?.some((c) => p === c.to || (!c.to.includes("#") && p.startsWith(`${c.to}/`))) && !MOBILE_NAV.some((m) => m.to === p)));

const Group = ({ n, i, pathname, onClose, anim }) => {
  const [open, setOpen] = useState(isActive(n, pathname) && !!n.children);
  const active = isActive(n, pathname);
  return (
    <motion.li {...anim(i)}>
      <div className="flex items-center justify-between">
        <Link
          to={n.to}
          onClick={onClose}
          data-testid={`mobile-nav-${n.key}`}
          aria-current={pathname === n.to ? "page" : undefined}
          className={`flex min-h-[52px] flex-1 items-baseline gap-4 font-display text-[2.15rem] font-light leading-tight ${active ? "text-coral-ink" : "text-ink"}`}
        >
          <span className="w-6 font-sans text-xs font-medium tabular-nums text-coral-ink">{String(i + 1).padStart(2, "0")}</span>
          {n.label}
        </Link>
        {n.children && (
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            aria-label={`${n.label} pages`}
            data-testid={`mobile-nav-${n.key}-toggle`}
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-ink/15 text-ink"
          >
            <ChevronDown className={`h-5 w-5 transition-transform duration-300 ${open ? "rotate-180" : ""}`} strokeWidth={1.5} />
          </button>
        )}
      </div>
      {n.children && (
        <div className={`grid transition-[grid-template-rows] duration-500 ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`} inert={!open}>
          <ul className="overflow-hidden pl-10">
            {n.children.map((c) => (
              <li key={c.label}>
                <Link
                  to={c.to}
                  onClick={onClose}
                  data-testid={`mobile-sub-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className={`flex min-h-[44px] items-center text-[15px] ${pathname === c.to ? "font-semibold text-ink" : "text-ink/75"}`}
                >
                  {c.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </motion.li>
  );
};

export default function MobileMenu({ open, onClose, pathname }) {
  const reduce = useReducedMotion();
  const closeRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  const anim = (i) => (reduce ? {} : { initial: { opacity: 0, y: 18 }, animate: { opacity: 1, y: 0 }, transition: { delay: 0.06 + i * 0.04, duration: 0.6, ease: [0.22, 1, 0.36, 1] } });

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          id="mobile-menu"
          role="dialog"
          aria-modal="true"
          aria-label="Site menu"
          data-testid="mobile-menu-overlay"
          className="fixed inset-0 z-50 overflow-y-auto bg-ivory"
          initial={reduce ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
        >
          <div aria-hidden className="pointer-events-none absolute -right-24 top-24 h-72 w-72 rounded-full bg-[radial-gradient(circle,rgba(224,122,85,0.28),transparent_68%)]" />
          <div className="relative mx-auto flex min-h-full max-w-2xl flex-col px-6 pb-10 pt-6">
            <div className="flex items-center justify-between">
              <img src={LOGO} alt="Feeling Moved — Body, Mind, Spirit" width="1138" height="183" className="h-6 w-auto" />
              <button
                ref={closeRef}
                type="button"
                onClick={onClose}
                aria-label="Close menu"
                data-testid="mobile-menu-close-button"
                className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-ink/15 text-ink"
              >
                <X className="h-6 w-6" strokeWidth={1.5} />
              </button>
            </div>

            <nav aria-label="Mobile" className="mt-10">
              <ul className="space-y-0.5">
                {MOBILE_NAV.map((n, i) => (
                  <Group key={n.key} n={n} i={i} pathname={pathname} onClose={onClose} anim={anim} />
                ))}
              </ul>
              <motion.div {...anim(9)} className="mt-8 grid gap-3 sm:grid-cols-2">
                <Link to={LINKS.contact} onClick={onClose} data-testid="mobile-nav-book-button" className="flex min-h-[56px] w-full items-center justify-center rounded-full bg-coral-ink text-base font-semibold text-ivory">
                  Book a Session
                </Link>
                <a
                  href={LINKS.login}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={onClose}
                  data-testid="mobile-nav-member-login-button"
                  className="flex min-h-[56px] w-full items-center justify-center rounded-full border border-ink/20 text-base font-semibold text-ink"
                >
                  Member Login<span className="sr-only"> (opens in a new tab)</span>
                </a>
              </motion.div>
            </nav>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
